/**
 * Permission gate logic hook
 * Handles secure permission checking with caching and audit logging
 */

import { useState, useCallback, useMemo } from 'react';
import { usePermissions } from '../../../lib/permissions/contexts/permission-provider';
import { permissionAudit } from '../../../lib/permissions/services/permission-audit';
import { securePermissionCache } from '../../../lib/permissions/services/secure-cache';
import { permissionIntegrity } from '../../../lib/permissions/services/permission-integrity';
import { componentLogger } from '@/lib/logging';
import type {
  PermissionRequirement,
  PermissionResource,
  PermissionAction
} from '../../../lib/permissions/types';

export type SecurityLevel = 'standard' | 'high' | 'maximum';

interface UsePermissionGateProps {
  requires?: string;
  action?: PermissionAction;
  resource?: PermissionResource;
  resourceId?: string;
  requirement?: PermissionRequirement;
  securityLevel?: SecurityLevel;
  auditLog?: boolean;
  serverSideValidation?: boolean;
}

export const usePermissionGate = ({
  requires,
  action,
  resource,
  resourceId,
  requirement: propRequirement,
  securityLevel = 'standard',
  auditLog = true,
  serverSideValidation = false,
}: UsePermissionGateProps) => {
  const { hasPermission, userRole, isSystemAdmin, userId } = usePermissions();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastValidation, setLastValidation] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Build permission requirement from props
  const buildRequirement = useCallback((): PermissionRequirement | null => {
    if (propRequirement) return propRequirement;

    if (requires) {
      const parts = requires.split('.');
      if (parts.length >= 2) {
        return {
          action: parts[1] as PermissionAction,
          resource: parts[0] as PermissionResource,
          ...(resourceId !== undefined && { resourceId })
        };
      }
    }

    if (action && resource) {
      return {
        action,
        resource,
        ...(resourceId !== undefined && { resourceId })
      };
    }

    return null;
  }, [propRequirement, requires, action, resource, resourceId]);

  // Generate cache key for this permission check
  const cacheKey = useMemo(() => {
    const req = propRequirement || buildRequirement();
    if (!req) return null;

    return `pgate_${userId}_${req.action}_${req.resource}_${req.resourceId || 'global'}`;
  }, [propRequirement, buildRequirement, userId]);

  // Security-hardened permission check
  const performSecurePermissionCheck = useCallback(async (
    requirement: PermissionRequirement,
    bypassCache = false
  ): Promise<boolean> => {
    const startTime = performance.now();
    let result = false;
    let cacheHit = false;

    // Early return if no user ID (not authenticated)
    if (!userId) {
      return false;
    }

    try {
      // System Admin bypass: Skip cache for security (prevents race conditions)
      if (isSystemAdmin) {
        if (auditLog) {
          permissionAudit.logPermissionCheck(
            requirement.companyScope,
            `${requirement.action}.${requirement.resource}`,
            requirement.resource,
            requirement.resourceId,
            'SUCCESS',
            'System Admin bypassing cache for enhanced security',
            {
              reason: 'system_admin_security_bypass',
              securityLevel: 'maximum',
              responseTime: performance.now() - startTime,
              userRole,
              isSystemAdmin
            }
          );
        }

        // Force fresh permission check for System Admin
        const permissionResult = await hasPermission(requirement);
        return permissionResult.allowed;
      }

      // Try cache first (unless bypassing)
      if (!bypassCache && cacheKey) {
        const cached = await securePermissionCache.get(cacheKey, userId);
        if (cached) {
          result = cached.permissions[`${requirement.action}.${requirement.resource}`] || false;
          cacheHit = true;
        }
      }

      // Perform actual permission check if no valid cache
      if (!cacheHit) {
        const permissionResult = await hasPermission(requirement);
        result = permissionResult.allowed;

        // Cache the result with cryptographic signing
        if (cacheKey && result && userId) {
          const permissionKey = `${requirement.action}.${requirement.resource}`;
          const permissions = { [permissionKey]: result };

          if (Object.keys(permissions).length > 0) {
            const signedState = await permissionIntegrity.signPermissionState(
              userId,
              permissions
            );

            await securePermissionCache.set(cacheKey, signedState, userId);
          }
        }
      }

      // Server-side validation for high-security deployments
      if (serverSideValidation && securityLevel === 'maximum') {
        // Note: In real implementation, this would call a server endpoint
        // Server-side validation would be performed here
      }

      // Audit log the permission check
      if (auditLog) {
        permissionAudit.logPermissionCheck(
          requirement.companyScope,
          `${requirement.action}.${requirement.resource}`,
          requirement.resource,
          requirement.resourceId,
          result ? 'SUCCESS' : 'DENIED',
          result ? 'Permission granted' : 'Permission denied',
          {
            cacheHit,
            responseTime: performance.now() - startTime,
            securityLevel,
            userRole,
            isSystemAdmin
          }
        );
      }

      return result;

    } catch (error) {
      // Log security error
      if (auditLog) {
        permissionAudit.logPermissionCheck(
          requirement.companyScope,
          `${requirement.action}.${requirement.resource}`,
          requirement.resource,
          requirement.resourceId,
          'FAILURE',
          `Permission check failed: ${error}`,
          {
            error: error instanceof Error ? error.message : 'Unknown error',
            securityLevel,
            responseTime: performance.now() - startTime
          }
        );
      }

      // Fail secure - deny on error
      return false;
    }
  }, [hasPermission, cacheKey, auditLog, serverSideValidation, securityLevel, userRole, isSystemAdmin, userId]);

  // Initialize permission check
  const checkPermission = useCallback(async (forceCheck = false) => {
    // Skip if already initialized and not forcing a recheck
    if (isInitialized && !forceCheck) {
      return;
    }

    setIsLoading(true);

    const requirement = buildRequirement();
    if (!requirement) {
      componentLogger.warn('permission-gate', 'invalid-requirement', { requirement: null });
      setIsAllowed(false);
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      const allowed = await performSecurePermissionCheck(requirement);
      setIsAllowed(allowed);
      setLastValidation(Date.now());
      setIsInitialized(true);
    } catch (error) {
      componentLogger.error('permission-gate', 'check-failed', error as Record<string, unknown>);
      setIsAllowed(false); // Fail secure
      setIsInitialized(true);
    } finally {
      setIsLoading(false);
    }
  }, [buildRequirement, performSecurePermissionCheck, isInitialized]);

  return {
    isAllowed,
    isLoading,
    lastValidation,
    checkPermission,
    buildRequirement,
    performSecurePermissionCheck,
    isInitialized,
  };
};